import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Award, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const CITIES = [
  "New York", "Los Angeles", "Chicago", "San Francisco", "Austin",
  "Portland", "Seattle", "Denver", "Miami", "Boston", "London", "Somewhere else",
];

const selectTextClass = (dark, placeholder) => {
  if (placeholder) return dark ? "text-cream/50" : "text-ink/40";
  return dark ? "text-cream" : "text-ink";
};

export const WaitlistForm = ({
  dark = false,
  testIdPrefix = "waitlist",
  submitLabel = "Join the waitlist",
  note = "The first 1,000 members in each city receive an exclusive Gold Status badge on their profile forever.",
}) => {
  const [city, setCity] = useState("");
  const typedCity = city === "Somewhere else";

  return (
    <form
      action="https://formspree.io/f/xljdzpzz"
      method="POST"
      className="w-full"
      data-testid={`${testIdPrefix}-form`}
    >
      <input type="hidden" name="_subject" value="Community Hearts waitlist" />
      <input type="hidden" name="_next" value="https://joincommunityhearts.com/?waitlist=1" />
      <div
        className={`flex flex-col gap-2.5 rounded-3xl border p-2 sm:flex-row sm:items-center sm:gap-2 sm:rounded-full sm:p-1.5 ${
          dark ? "border-cream/25 bg-cream/5" : "border-ink/15 bg-white/60"
        }`}
      >
        <Input
          type="email"
          name="email"
          required
          placeholder="Your email address"
          aria-label="Email address"
          data-testid={`${testIdPrefix}-email-input`}
          className={`h-12 min-w-0 flex-1 rounded-full border-0 bg-transparent px-5 text-base shadow-none focus-visible:ring-2 focus-visible:ring-gold ${
            dark
              ? "text-cream placeholder:text-cream/50"
              : "text-ink placeholder:text-ink/40"
          }`}
        />
        <Button
          type="submit"
          data-testid={`${testIdPrefix}-submit-button`}
          className="btn-glow h-12 w-full shrink-0 whitespace-nowrap rounded-full px-7 text-sm font-bold uppercase tracking-[0.15em] bg-gold text-ink hover:bg-gold-deep active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
        >
          {submitLabel}
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
      <label
        className={`mt-3 flex cursor-pointer items-center gap-3 rounded-2xl border px-4 py-3 text-sm leading-relaxed transition-colors ${
          dark ? "border-cream/20 bg-cream/5 text-cream/80" : "border-ink/15 bg-white/60 text-ink/80"
        }`}
        data-testid={`${testIdPrefix}-age-label`}
      >
        <input
          type="checkbox"
          required
          data-testid={`${testIdPrefix}-age-checkbox`}
          className="h-5 w-5 shrink-0 cursor-pointer rounded border-gold/50 bg-transparent accent-gold focus-visible:ring-2 focus-visible:ring-gold"
        />
        <span>I confirm that I am 18 years of age or older.</span>
      </label>
      <div className={`relative mt-3 flex items-center rounded-full border ${dark ? "border-cream/25 bg-cream/5" : "border-ink/15 bg-white/60"}`}>
        <select
          name={typedCity ? undefined : "city"}
          required
          value={city}
          onChange={(e) => setCity(e.target.value)}
          aria-label="Your city"
          data-testid={`${testIdPrefix}-city-select`}
          className={`h-12 w-full cursor-pointer appearance-none rounded-full bg-transparent px-5 pr-10 text-sm font-semibold outline-none ${selectTextClass(dark, city === "")}`}
        >
          <option value="" disabled className="text-ink">Your city</option>
          {CITIES.map((c) => (
            <option key={c} value={c} className="text-ink">{c}</option>
          ))}
        </select>
        <ChevronDown className={`pointer-events-none absolute right-4 h-4 w-4 ${dark ? "text-cream/60" : "text-ink/40"}`} />
      </div>
      {typedCity && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <Input
            type="text"
            name="city"
            required
            maxLength={80}
            placeholder="Which city should we come to?"
            aria-label="Your city name"
            data-testid={`${testIdPrefix}-custom-city-input`}
            className={`mt-3 h-12 rounded-full border px-5 text-base focus-visible:ring-2 focus-visible:ring-gold ${
              dark
                ? "border-cream/25 bg-cream/5 text-cream placeholder:text-cream/50"
                : "border-ink/15 bg-white/60 text-ink placeholder:text-ink/40"
            }`}
          />
        </motion.div>
      )}
      <p
        className={`mt-4 flex items-start gap-2 text-xs leading-relaxed ${dark ? "text-cream/50" : "text-ink/50"}`}
        data-testid={`${testIdPrefix}-gold-status-note`}
      >
        <Award className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${dark ? "text-gold" : "text-gold-deep"}`} />
        {note}
      </p>
    </form>
  );
};
